import { binarySearch } from "@mimir/common/util/binarySearch";

export interface FrameRange {
  // Start frame of range (inclusive)
  start: number,
  // End frame of range (exclusive)
  end: number,
}

export interface ReverseGopWarmUpInfo {
  decodedRanges: FrameRange[],
  frameNumber: number,
  decodePosition: number | null,
  gopSize: number,
}

export interface ResultSeek {
  type: 'seek',
  frameNumber: number,
}

export interface ResultProgress {
  type: 'progress'
}

export type Result = ResultSeek | ResultProgress;

export function analyzeReverseGopWarmUp(info: ReverseGopWarmUpInfo): Result | null {
  /// GOP structure:    |---------|---------|---------|---------|---------|
  //  Decoded content:    ###     #####     #########
  //                                  ^    ^    ^
  //                                  |    |    +--- frameNumber
  //                                  |    +-------- firstMiss
  //                                  +--------------decodePosition
  //
  // If the number of frames between frameNumber and firstMiss is less
  // or equal to the gap between decodePosition and decodeDownTo we are ok.
  // We need to check all gaps within a two-gop range to ensure smooth 

  // Calculate the decoded ranges from fractions of time to frame numbers.
  // The calculations in the rest of the function is in frame numbers.

  const { decodedRanges, frameNumber, decodePosition, gopSize } = info;

  let index =
    binarySearch<{ end: number }>(decodedRanges, { end: frameNumber }, (a, b) => a.end - b.end);

  let firstMiss : number;
  if (index === -1) {
    // No ranges with end greater or equal to frame rate. There is no decoded
    // content at the given position:
    firstMiss = frameNumber;
  } else if (decodedRanges[index].end === frameNumber) {
    // If a range ends exactly on our frame number it means we have no
    // decoded content at the current frame hence:
    firstMiss = frameNumber;
  } else if (decodedRanges[index].start > frameNumber) {
    // The range is beyond our frame number:
    firstMiss = frameNumber;
  } else {
    firstMiss = decodedRanges[index].start - 1;
  }


  if (frameNumber - firstMiss >= gopSize) {
    // Have cached enough frames
    return null;
  }

  if (decodePosition && (decodePosition > firstMiss)) {
    // The decode position is not on the right side of the play direction
    // hence it is useless. We need to start fetching at the current
    // decodedDownTo position:
    return { type: 'seek', frameNumber: firstMiss };
  }

  if (decodePosition === null) {
    // We need to process more packet to determine where we are
    return { type: 'progress' };
  }

  if ((firstMiss - decodePosition) >= (frameNumber - firstMiss)) {
    // The undecoded gap is still too large
    return { type: 'progress' };
  }

  // Also check the previous range starts within a range of a gop
  index --;
  if (index >= 0) {
    const previousRange = decodedRanges[index];
    if ((frameNumber - previousRange.start) < gopSize) {
      return { type: 'progress' }
    }
  }

  // No need to do anything
  return null;
}
